/**
 * 注册参数组装纯函数。
 *
 * 组装 params 的纯逻辑从 store/register.ts 的 toRegister() 提取至此，
 * 便于离线单测（规避 mobx store / 路径别名 / 副作用依赖的脆弱性）。
 *
 * 【约定】
 *   - invite_code 恒带（与现状一致，即使为空串）
 *   - captcha 走 captchaOverride（现有 'xxxx' 免验证关键字）
 *   - utm_params 为空传 null（与现状 isEmpty ? null 一致）
 *
 * 【测试】src/store/__tests__/register-params.test.ts
 *
 * @author 薪智
 */

/** 注册表单字段值（store/register.ts 的 observable 字段子集） */
export interface RegisterFieldValues {
  company: string;    // 所在公司
  email: string;      // 注册邮箱
  phone: string;      // 手机号码
  captcha: string;    // 短信验证码
  inviteCode: string; // 邀请码（非必填）
}

/** 组装参数时外部注入的上下文（URL query / referer / sessionStorage 等） */
export interface BuildRegisterParamsOptions {
  /** 验证码覆盖值（现有 'xxxx' 免验证关键字） */
  captchaOverride?: string;
  /** URL 活动 id */
  activityUuid?: string;
  /** 注册来源（baidu / referer / query） */
  from?: string;
  /** UTM 跟踪参数（sessionStorage registerQuery） */
  utmParams?: Record<string, unknown> | null;
}

/** 提交给后端 /mg/users/register 的请求体 */
export interface RegisterParams {
  company: string;
  email: string;
  phone: string;
  captcha: string;
  activity_uuid: string;
  from: string;
  invite_code: string;
  utm_params: Record<string, unknown> | null;
}

/**
 * 组装注册请求 params。
 *
 * @param fields 表单字段值
 * @param opts   外部上下文
 * @returns 后端请求体
 */
export function buildRegisterParams(
  fields: RegisterFieldValues,
  opts: BuildRegisterParamsOptions,
): RegisterParams {
  const { company, email, phone, captcha, inviteCode } = fields;
  const {
    captchaOverride, activityUuid, from, utmParams,
  } = opts;

  const params: RegisterParams = {
    company,
    email,
    phone,
    // 中台给了 'xxxx' 作为免验证的关键字（对齐 toRegister 现状）
    captcha: captchaOverride ?? captcha,
    activity_uuid: activityUuid || '',
    from: from || '',
    // 邀请码恒带（现状语义：即使空串也传）
    invite_code: inviteCode,
    utm_params: utmParams ?? null,
  };

  return params;
}
